// @ts-strict-ignore
import * as React from 'react';

import Modal from 'antd/lib/modal';
import ConfigProvider from 'antd/lib/config-provider';
import Form, { useForm, useWatch } from 'antd/lib/form/Form';
import Space from 'antd/lib/space';
import FormItem from 'antd/lib/form/FormItem';
import Text from 'antd/lib/typography/Text';
import antdTheme from 'antd/lib/theme';
import * as API from '../../api';
import { useSelector } from 'react-redux';
import { CreateEditReleaseType, CreateHotfixOrRollbackReleaseRequest } from '../../actions/release';
import { AppComponentProps, AppRouteComponentMatchParams } from '../../typescriptHelpers';
import {
    selectAppStoreType,
    selectAuthCredentials,
    selectIntegrationForCapability,
    selectIsDemoInstanceOrDemoModeEnabled,
    selectWorkingBranch,
} from '../../selectors/selectors';
import { RootState } from '../../reducers/reducers';
import {
    HotfixCheckboxTypes,
    IntegrationCapability,
    IntegrationType,
    ReleaseTag,
    SubscriptionFeatureType,
    AllRollbackResigningStrategies,
    RollbackResigningStrategy,
} from '../../types';
import Radio from 'antd/lib/radio';
import Checkbox from 'antd/lib/checkbox';
import { CommitSelector } from '../CommitSelectorComponent';
import Divider from 'antd/lib/divider';
import { GetPossibleBaseTagsRequest, GetPossibleSourceVersionsForRollbackRequest } from '../../actions/app';
import { appStoreSupportsRollbacks, getIsGooglePlayStore } from '../../helpers';
import { globalMessage } from '../../utils/globalMessage';
import { useHistory, useParams } from 'react-router-dom';
import Select from 'antd/lib/select';
import { createAppRoute } from '../../helpers/routeHelpers';
import * as Storage from '../../helpers/localStorageHelper';
import { BetaBadge } from '../automations/AppAutomationSettings';
import { InfoCircleOutlined } from '@ant-design/icons';
import { antGrey, getAntdThemeTokens } from '../styles/globalStyles';
import { PlanRestrictedFeatureWrapper } from '../planRestrictions/PlanRestrictedFeatureWrapper';
import { displayTextForResigningStrategy } from '@/helpers/displayHelpers';
import { isValidCommitHashFormat } from '@/helpers/formHelpers';
import { useVersionValidation } from '../../utils/hooks/useVersionValidation';
import { usePredictVersion } from '../../utils/hooks/usePredictVersion';
import { useReleaseCreation } from '../../utils/hooks/useReleaseCreation';
import { ReleaseVersionFormItem } from '../prepareRelease/ReleaseVersionFormItem';
import { ReleaseNameFormItem } from '../prepareRelease/ReleaseNameFormItem';
import { MatchPhasedReleaseCheckbox } from '../prepareRelease/MatchPhasedReleaseCheckbox';
import { useThemeMode } from '../styles/themeMode';

interface PrepareHotfixOrRollbackReleaseModalProps extends AppComponentProps {
    open: boolean;
    isConvertingRollback: boolean;
    releaseId?: string;
    releaseVersion?: string;
    releaseName?: string;
    onCancel: () => void;
}

const PrepareHotfixOrRollbackReleaseModal: React.FC<PrepareHotfixOrRollbackReleaseModalProps> = (
    props: PrepareHotfixOrRollbackReleaseModalProps,
) => {
    const history = useHistory();
    const { orgIdParam, appIdParam } = useParams<AppRouteComponentMatchParams>();
    const { mode } = useThemeMode();

    const authCredentials = useSelector((state: RootState) => selectAuthCredentials(state));

    const [selectedHotfixReleaseType, setSelectedHotfixReleaseType] = React.useState<CreateEditReleaseType>(
        CreateEditReleaseType.Hotfix,
    );
    const [branchHelp, setBranchHelp] = React.useState('');
    const [branchName, setBranchName] = React.useState('');
    const [versionStringForPossibleBump, setVersionStringForPossibleBump] = React.useState('');
    const [bumpVersionHelp, setBumpVersionHelp] = React.useState('');

    const showCommitSelector = useSelector((state: RootState) => {
        const vcsIntegration = selectIntegrationForCapability(state, props.appId, IntegrationCapability.VersionControl);

        return vcsIntegration?.id === IntegrationType.GitHubVCS || vcsIntegration?.id === IntegrationType.GitLabVCS;
    });

    const workingBranch = useSelector((state: RootState) => selectWorkingBranch(state, props.appId));
    const isDemoMode = useSelector((state: RootState) => selectIsDemoInstanceOrDemoModeEnabled(state, props.orgId));
    const appStoreType = useSelector((state: RootState) => selectAppStoreType(state, props.appId));

    const [commits, setCommits] = React.useState([]);
    const [fetchCommitsError, setFetchCommitsError] = React.useState(null);

    const [commitsLoading, setCommitsLoading] = React.useState(false);
    const [resultBranchName, setResultBranchName] = React.useState('');
    const [resultIsStaticBranch, setResultIsStaticBranch] = React.useState(false);
    const [previousTag, setPreviousTag] = React.useState('');
    const [latestCompletedReleaseVersion, setLatestCompletedReleaseVersion] = React.useState('');
    const [possibleSourceVersionsForRollback, setPossibleSourceVersionsForRollback] = React.useState<string[]>([]);
    const [possibleBasesForHotfix, setPossibleBasesForHotfix] = React.useState<ReleaseTag[]>([]);
    const [baseForHotfix, setBaseForHotfix] = React.useState<ReleaseTag>(null);
    const [shouldMatchPreviousPhasedRelease, setShouldMatchPreviousPhasedRelease] = React.useState(false);
    const [commitSearchValue, setCommitSearchValue] = React.useState('');
    const [predictedHotfixBase, setPredictedHotfixBase] = React.useState<string | null>(null);

    const [form] = useForm();

    const selectedCommits = useWatch('commitHashes', form);

    const {
        isCreatingRelease: prepareNewHotfixOrRollbackIsLoading,
        createReleaseSuccess: prepareNewHotfixOrRollbackSuccess,
        createReleaseError: prepareNewHotfixOrRollbackFailure,
        createNewReleaseWithConversion,
    } = useReleaseCreation({
        orgId: props.orgId,
        appId: props.appId,
        releaseId: props.releaseId,
    });

    const { validateVersion: doValidateVersion } = useVersionValidation({
        orgId: props.orgId,
        appId: props.appId,
        releaseId: props.releaseId,
        releaseType: selectedHotfixReleaseType,
    });

    const { predictVersion } = usePredictVersion({
        orgId: props.orgId,
        appId: props.appId,
        releaseId: props.releaseId,
    });

    const isGooglePlayStore = getIsGooglePlayStore(appStoreType);
    const isHotfix = selectedHotfixReleaseType === CreateEditReleaseType.Hotfix;
    const shouldShowShouldMatchPreviousPhasedReleaseOption = isHotfix && isGooglePlayStore;

    const shouldFetchData = !isDemoMode;

    const prepareHotfixOrRollback = (values) => {
        if (values) {
            const localStorageData = {
                shouldCreateBranch: values[HotfixCheckboxTypes.ShouldCreateBranch],
                shouldBumpVersionOnBranch: values[HotfixCheckboxTypes.ShouldBumpVersionOnBranch],
            };
            Storage.setItem(appIdParam, localStorageData);
        }

        const prepareHotfixOrRollbackRequest: CreateHotfixOrRollbackReleaseRequest = {
            orgId: props.orgId,
            appId: props.appId,
            releaseType: selectedHotfixReleaseType,
            shouldCreateBranch: values.shouldCreateBranch ?? false,
            shouldBumpVersionOnBranch: values.shouldBumpVersionOnBranch ?? false,
            version: values.releaseVersion,
            releaseName: values.releaseName,
            commitsToCherryPick: values.commitHashes ?? [],
            sourceVersion: values.sourceVersion,
            base: baseForHotfix,
            resigningStrategy: values.resigningStrategy,
            shouldMatchPreviousPhasedRelease,
        };

        createNewReleaseWithConversion(prepareHotfixOrRollbackRequest, props.isConvertingRollback, props.releaseId);
    };

    const versionRegex = /\d+(?:\.\d+)?(?:\.\d+)?/;

    const setBranchHelpDefaults = () => {
        setBranchHelp('Immediately create release branch from previous release tag');
    };

    const setBumpHelpDefaults = () => {
        setBumpVersionHelp('Bump version on release branch');
    };

    const baseTagSelected = (selectedTag) => {
        const releaseTag = possibleBasesForHotfix.find((v) => v.name == selectedTag);
        if (releaseTag) {
            setBaseForHotfix(releaseTag);
            form.validateFields(['baseForHotfix']);
            getCandidateCherryPickCommits(releaseTag.version);
        }
    };

    const handleVersionValidation = async (version: string): Promise<void> => {
        const values = form.getFieldsValue();
        try {
            const result = await doValidateVersion(version, values.sourceVersion);

            setResultIsStaticBranch(result.isStaticBranch);
            setResultBranchName(result.branchName);
            setPreviousTag(result.hotfixBase);
            setLatestCompletedReleaseVersion(result.latestCompletedReleaseVersion);

            if (result.hotfixBase) {
                setPredictedHotfixBase(result.hotfixBase);
            }

            if (!result.hotfixPreviousVersion && selectedHotfixReleaseType === CreateEditReleaseType.Hotfix) {
                throw new Error('No previous completed release to prepare hotfix from');
            }

            if (result.branchName) {
                setBranchName(result.branchName);
            }
            if (result.previousTags) {
                setPossibleBasesForHotfix(result.previousTags);
            }
            if (result.versionStringForPossibleBump) {
                setVersionStringForPossibleBump(result.versionStringForPossibleBump);
            }
        } catch (error) {
            setBranchHelpDefaults();
            setBumpHelpDefaults();
            throw error;
        }
    };

    const validateSourceVersion = (rule, sourceVersion) => {
        if (!sourceVersion || !versionRegex.test(sourceVersion)) {
            return Promise.reject('Source version must contain only numbers and up to 3 segments');
        }

        return Promise.resolve();
    };

    const populatePredictedVersion = async (isHotfix: boolean) => {
        const version = await predictVersion(isHotfix);
        form.setFieldsValue({ releaseVersion: version });
    };

    const populatePossibleTagsForHotfix = React.useCallback(() => {
        const request: GetPossibleBaseTagsRequest = {
            orgId: props.orgId,
            appId: props.appId,
        };

        return API.getPossibleBaseTags(request, authCredentials)
            .then((sourceTags) => {
                setPossibleBasesForHotfix(sourceTags);
            })
            .catch(() => {
                setPossibleBasesForHotfix([]);
            });
    }, [authCredentials]);

    const getPossibleSourceVersionsForRollback = React.useCallback(() => {
        const request: GetPossibleSourceVersionsForRollbackRequest = {
            orgId: props.orgId,
            appId: props.appId,
        };

        return API.getPossibleSourceVersions(request, authCredentials)
            .then((sourceVersions) => {
                setPossibleSourceVersionsForRollback(sourceVersions);

                if (sourceVersions.length > 0) {
                    form.setFieldValue('sourceVersion', sourceVersions[0]);
                }
            })
            .catch(() => {
                setPossibleSourceVersionsForRollback([]);
            });
    }, [authCredentials]);

    const getCandidateCherryPickCommits = React.useCallback(
        (version: string) => {
            setCommitsLoading(true);
            API.getCandidateSelectableCommits(
                { appId: props.appId, orgId: props.orgId, version: version },
                authCredentials,
            )
                .then((response) => {
                    setCommits(response.commits);
                    setCommitsLoading(false);
                })
                .catch((error) => {
                    setCommitsLoading(false);
                    setFetchCommitsError(error.message);
                });
        },
        [authCredentials],
    );

    const searchForCommit = React.useCallback(
        async (commitHash: string) => {
            setCommitsLoading(true);

            try {
                const response = await API.getCandidateSelectableCommits(
                    { appId: props.appId, orgId: props.orgId, commitHash: commitHash },
                    authCredentials,
                );

                if (response.commits.length === 0) {
                    return;
                }

                const commitFound = response.commits[0];
                if (commits.find((commit) => commit.hash === commitFound.hash)) {
                    return;
                }

                setCommits([...commits, commitFound]);
            } catch (error) {
                setFetchCommitsError(error.message);
            } finally {
                setCommitsLoading(false);
            }
        },
        [authCredentials, commits],
    );

    React.useEffect(() => {
        if (isValidCommitHashFormat(commitSearchValue)) {
            searchForCommit(commitSearchValue);
        }
    }, [commitSearchValue]);

    const updateSelectedHotfixType = (releaseType: CreateEditReleaseType) => {
        setSelectedHotfixReleaseType(releaseType);
    };
    const hotfixStoredValues = Storage.getItem(appIdParam);

    const initialValues = {
        releaseVersion: null,
        shouldCreateBranch: hotfixStoredValues ? hotfixStoredValues[HotfixCheckboxTypes.ShouldCreateBranch] : true,
        shouldBumpVersionOnBranch:
            hotfixStoredValues ? hotfixStoredValues[HotfixCheckboxTypes.ShouldBumpVersionOnBranch] : true,
        releaseName: null,
        sourceVersion: null,
        baseForHotfix: null,
    };

    React.useEffect(() => {
        if (branchName && baseForHotfix) {
            setBranchHelp("Immediately create branch '" + branchName + "' from tag " + baseForHotfix.name);
        } else {
            setBranchHelpDefaults();
        }
        if (versionStringForPossibleBump) {
            setBumpVersionHelp("Bump version on branch '" + branchName + "' to " + versionStringForPossibleBump);
        } else {
            setBumpHelpDefaults();
        }
    }, [branchName, versionStringForPossibleBump, baseForHotfix]);

    React.useEffect(() => {
        if (props.open) {
            setPredictedHotfixBase(null);
            setSelectedHotfixReleaseType(CreateEditReleaseType.Hotfix);

            if (props.releaseId && props.releaseVersion && props.isConvertingRollback) {
                form.setFieldsValue({
                    ...initialValues,
                    releaseVersion: props.releaseVersion,
                    releaseName: props.releaseName,
                });

                form.validateFields(['releaseVersion', 'releaseName']);
            } else {
                form.setFieldsValue(initialValues);

                if (shouldFetchData) {
                    const promises = [populatePredictedVersion(true), populatePossibleTagsForHotfix()];

                    Promise.all(promises).then(() => {
                        form.validateFields(['releaseVersion', 'releaseName']);
                    });
                }
            }
        }
    }, [props.open, props.releaseId, props.isConvertingRollback]);

    React.useEffect(() => {
        if (prepareNewHotfixOrRollbackFailure && props.open) {
            globalMessage.error(prepareNewHotfixOrRollbackFailure);
        }
    }, [prepareNewHotfixOrRollbackFailure]);

    React.useEffect(() => {
        if (prepareNewHotfixOrRollbackSuccess && props.open) {
            const successMsg =
                selectedHotfixReleaseType === CreateEditReleaseType.Hotfix ?
                    'New roll-forward hotfix created!'
                :   'New rollback hotfix created!';
            globalMessage.success(successMsg);

            props.onCancel();

            if (props.isConvertingRollback) {
                Storage.clearLastSelectedReleaseStepForRelease(props.releaseId);

                const appRoute = createAppRoute(orgIdParam, appIdParam);

                history.replace(appRoute);
            }
        }
    }, [prepareNewHotfixOrRollbackSuccess]);

    React.useEffect(() => {
        if (props.open) {
            setBranchHelpDefaults();
            setBumpHelpDefaults();

            if (shouldFetchData) {
                getCandidateCherryPickCommits('');
            }

            if (!props.isConvertingRollback && shouldFetchData) {
                getPossibleSourceVersionsForRollback();
            }
        }
    }, [props.open, authCredentials]);

    React.useEffect(() => {
        if (possibleBasesForHotfix?.length > 0) {
            if (predictedHotfixBase) {
                const predictedTag = possibleBasesForHotfix.find((tag) => tag.name === predictedHotfixBase);
                if (predictedTag && !baseForHotfix) {
                    setBaseForHotfix(predictedTag);
                    return;
                }
            }

            if (!baseForHotfix) {
                const firstTag = possibleBasesForHotfix[0];
                setBaseForHotfix(firstTag);
            }
        }
    }, [possibleBasesForHotfix, predictedHotfixBase]);

    React.useEffect(() => {
        if (baseForHotfix) {
            form.setFieldValue('baseForHotfix', baseForHotfix.name);
        }
    }, [baseForHotfix]);

    React.useEffect(() => {
        setCommitSearchValue('');
    }, [selectedCommits]);

    const title = props.isConvertingRollback ? 'Convert rollback to roll-forward hotfix' : 'Prepare a hotfix';
    const buttonTitle =
        props.isConvertingRollback && props.releaseId ?
            `Convert ${props.releaseVersion} to roll-forward hotfix`
        :   `Prepare hotfix`;

    const showRollbackOption = !props.isConvertingRollback && appStoreSupportsRollbacks(appStoreType);
    const contentTheme = mode === 'dark' ? 'dark' : 'light';

    return (
        <ConfigProvider
            theme={{
                algorithm: contentTheme === 'dark' ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
                token: getAntdThemeTokens(contentTheme),
            }}
        >
            <Modal
                open={props.open}
                title={title}
                okText={buttonTitle}
                okButtonProps={{ loading: prepareNewHotfixOrRollbackIsLoading }}
                onOk={() => form.submit()}
                confirmLoading={prepareNewHotfixOrRollbackIsLoading}
                onCancel={props.onCancel}
                width={650}
            >
                <Form
                    form={form}
                    initialValues={initialValues}
                    onFinish={prepareHotfixOrRollback}
                    className="prepare-hotfix"
                >
                    {props.isConvertingRollback && (
                        <div style={{ marginBottom: 20 }}>
                            <Text strong>
                                Converting {props.releaseVersion} to a roll-forward hotfix will discard this rollback.
                            </Text>
                        </div>
                    )}
                    <ReleaseVersionFormItem onValidate={handleVersionValidation} />
                    <ReleaseNameFormItem />
                    <Divider />
                    <Radio.Group
                        onChange={(e) => updateSelectedHotfixType(e.target.value as CreateEditReleaseType)}
                        value={selectedHotfixReleaseType}
                        style={{
                            width: '100%',
                        }}
                    >
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                            <Radio value={CreateEditReleaseType.Hotfix}>
                                Create a hotfix release by{' '}
                                {resultIsStaticBranch ? 'fixing forward' : 'branching off a previous tag'}
                            </Radio>
                            {selectedHotfixReleaseType === CreateEditReleaseType.Hotfix && (
                                <div style={{ marginLeft: 20, display: 'flex', flexDirection: 'column' }}>
                                    {!resultIsStaticBranch && (
                                        <FormItem
                                            label="Base"
                                            name="baseForHotfix"
                                            validateTrigger="onSubmit"
                                            rules={[
                                                {
                                                    required: true,
                                                    message: 'You must pick a valid base tag to prepare hotfix',
                                                },
                                            ]}
                                        >
                                            <Select
                                                options={possibleBasesForHotfix?.map((releaseTag) => {
                                                    const isLatestRelease =
                                                        releaseTag.version === latestCompletedReleaseVersion;
                                                    return {
                                                        label:
                                                            isLatestRelease ?
                                                                `${releaseTag.name} (latest release)`
                                                            :   releaseTag.name,
                                                        value: releaseTag.name,
                                                    };
                                                })}
                                                onChange={baseTagSelected}
                                                placeholder="Select a base"
                                            />
                                        </FormItem>
                                    )}
                                    {!resultIsStaticBranch && (
                                        <FormItem
                                            label={null}
                                            name="shouldCreateBranch"
                                            valuePropName="checked"
                                            extra={branchHelp}
                                            rules={[{ required: false }]}
                                        >
                                            <Checkbox>Create branch</Checkbox>
                                        </FormItem>
                                    )}
                                    <FormItem
                                        label={null}
                                        name="shouldBumpVersionOnBranch"
                                        valuePropName="checked"
                                        extra={bumpVersionHelp}
                                        rules={[
                                            {
                                                required: false,
                                                validator: (_, shouldBumpVersion) => {
                                                    if (shouldBumpVersion) {
                                                        const createBranch = form.getFieldValue('shouldCreateBranch');

                                                        if (!createBranch && !resultIsStaticBranch) {
                                                            return Promise.reject(
                                                                "Cannot automatically bump version if 'Create branch' is not also enabled",
                                                            );
                                                        }
                                                    }

                                                    return Promise.resolve();
                                                },
                                            },
                                        ]}
                                        dependencies={['shouldCreateBranch']}
                                    >
                                        <Checkbox>Bump version</Checkbox>
                                    </FormItem>
                                    <FormItem
                                        label="Fixes to cherry-pick"
                                        tooltip={{
                                            title: `Selected fixes will be immediately cherry-picked onto ${
                                                resultBranchName ?
                                                    ` the '${resultBranchName}' hotfix release branch`
                                                :   ' the hotfix release branch.'
                                            }`,
                                            icon: <InfoCircleOutlined style={{ color: antGrey.grey7 }} />,
                                        }}
                                        name="commitHashes"
                                        hidden={!showCommitSelector}
                                        rules={[{ required: false }]}
                                        extra={
                                            fetchCommitsError ?
                                                <Text type="danger">Error fetching commits</Text>
                                            :   `Commits on ${
                                                    workingBranch ? `'${workingBranch}'` : `working branch`
                                                } since last tag${previousTag ? ` ${previousTag}` : ''}.`
                                        }
                                    >
                                        <CommitSelector
                                            allowSearch
                                            searchValue={commitSearchValue}
                                            onSearch={setCommitSearchValue}
                                            loading={commitsLoading}
                                            commits={commits}
                                            style={{ width: '100%' }}
                                            multiSelect={true}
                                            disabled={false}
                                        />
                                    </FormItem>
                                </div>
                            )}
                            {showRollbackOption && (
                                <PlanRestrictedFeatureWrapper
                                    feature={SubscriptionFeatureType.Rollbacks}
                                    requiresTooltip
                                >
                                    {(isDisabled) => (
                                        <>
                                            <Radio value={CreateEditReleaseType.Rollback} disabled={isDisabled}>
                                                <span
                                                    style={{
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        gap: '8px',
                                                    }}
                                                >
                                                    Create a hotfix release by rolling back to a previous stable build{' '}
                                                    <BetaBadge />
                                                </span>
                                            </Radio>
                                            {selectedHotfixReleaseType === CreateEditReleaseType.Rollback && (
                                                <Space direction="vertical">
                                                    <FormItem
                                                        label="Source version"
                                                        name="sourceVersion"
                                                        labelCol={{ flex: '140px' }}
                                                        rules={[
                                                            {
                                                                required: true,
                                                                message: '',
                                                            },
                                                            {
                                                                validator: validateSourceVersion,
                                                            },
                                                        ]}
                                                        style={{ marginLeft: 20 }}
                                                    >
                                                        <Select
                                                            options={possibleSourceVersionsForRollback.map(
                                                                (version, index) => {
                                                                    const isFirst = index === 0;

                                                                    return {
                                                                        label: `${version}${
                                                                            isFirst ?
                                                                                ' (latest stable, recommended)'
                                                                            :   ''
                                                                        }`,
                                                                        value: version,
                                                                    };
                                                                },
                                                            )}
                                                            placeholder="Select a source version"
                                                        />
                                                    </FormItem>
                                                    <FormItem
                                                        label="Strategy"
                                                        name="resigningStrategy"
                                                        labelCol={{ flex: '140px' }}
                                                        style={{ marginLeft: 20 }}
                                                    >
                                                        <Select
                                                            style={{ width: '100%' }}
                                                            defaultValue={RollbackResigningStrategy.LocalResign}
                                                        >
                                                            {AllRollbackResigningStrategies.map((option) => {
                                                                return (
                                                                    <Select.Option value={option} key={option}>
                                                                        {displayTextForResigningStrategy(option)}
                                                                    </Select.Option>
                                                                );
                                                            })}
                                                        </Select>
                                                    </FormItem>
                                                </Space>
                                            )}
                                        </>
                                    )}
                                </PlanRestrictedFeatureWrapper>
                            )}
                        </div>
                    </Radio.Group>
                    <Divider />
                    <MatchPhasedReleaseCheckbox
                        visible={shouldShowShouldMatchPreviousPhasedReleaseOption}
                        onChange={setShouldMatchPreviousPhasedRelease}
                    />
                </Form>
            </Modal>
        </ConfigProvider>
    );
};

export default PrepareHotfixOrRollbackReleaseModal;
